import ReactGA4 from 'react-ga4';
import { User } from '@fusion/auth';
import { set } from 'lodash';
import { TelemetryPluginWithConfig } from '../types';

export type InitOptions = {
  trackingId: string;
  gaOptions?: GaOptions | any;
  gtagOptions?: any;
};

export type GaOptions = {
  cookieUpdate?: boolean;
  /**
   * Default two years
   */
  cookieExpires?: number;
  cookieDomain?: string;
  cookieFlags?: string;
  userId?: string;
  clientId?: string;
  anonymizeIp?: boolean;
  contentGroup1?: string;
  contentGroup2?: string;
  contentGroup3?: string;
  contentGroup4?: string;
  contentGroup5?: string;
  allowAdFeatures?: boolean;
  allowAdPersonalizationSignals?: boolean;
  nonInteraction?: boolean;
  page?: string;
};

export type Ga4PluginConfig = {
  trackers: Array<{
    /** ReactGA config */
    initOptions: InitOptions;

    /** Whether to automatically add the user id (if available) to the GA config */
    sendUserId: boolean;

    /** Optional callback for setting additional dimensions */
    getCustomDimensions?: (user?: User) => Record<string, string> | undefined;
  }>;

  /**
   * Global options for all trackers. "gaOptions" and "gtagOptions" are applied
   * for each tracker but are overriden if a value is provided in that tracker's
   * "initOptions".
   */
  options?: {
    nonce?: string;
    testMode?: boolean;
    gtagUrl?: string;
    gaOptions?: GaOptions;
    gtagOptions?: any;
  };
};

const name = 'ga4';

declare module './Plugins' {
  interface Plugins {
    [name]: { ReactGA4: typeof ReactGA4 };
  }
}

export type Ga4PluginCtx = {
  ReactGA4: typeof ReactGA4;
};

export const Ga4Plugin: TelemetryPluginWithConfig<Ga4PluginConfig, Ga4PluginCtx> = ({ trackers, options = {} }) => {
  return {
    name,

    init({ user }) {
      for (const tracker of trackers) {
        const { sendUserId, initOptions, getCustomDimensions } = tracker;

        if (sendUserId && user && user.id) {
          set(initOptions, 'gaOptions.userId', user.id);
        }

        if (getCustomDimensions) {
          const dimensions = getCustomDimensions(user);

          if (dimensions) {
            Object.entries(dimensions).forEach(([key, value]) => {
              set(initOptions, `gaOptions.${key}`, value);
            });
          }
        }
      }

      ReactGA4.initialize(
        trackers.map(({ initOptions }) => initOptions),
        options,
      );

      return {
        ReactGA4,
      };
    },

    onRouteChange(/* { url, title } */) {
      /**
       * GA4 sends page_view events automatically when the browser history changes
       * NB: this behaviour is not visible when working locally, it only works
       * when GA4 is initialised with a valid tracking ID. Uncomment this block
       * for local testing if needed.
       *
       * In staging/prod, this means that this route handler, which works well for
       * other plugins, sends a duplicate page view event.
       * Disabling it for now, but this needs revisiting when we refactor this
       * telemetry plugin system.
       */
      // ReactGA4.send({ hitType: 'pageview', page: url, title });
    },
  };
};
